import { create } from 'zustand'
import type { DirectConversation, DirectMessage, UserPresence } from '../types'

interface DMState {
  // Lista de conversas
  conversations: DirectConversation[]
  selectedConversationId: string | null
  
  // Mensagens da conversa selecionada
  messages: DirectMessage[]
  isLoadingMessages: boolean
  hasMoreMessages: boolean
  
  // Contador total de não lidas
  totalUnreadCount: number
  
  // Presença online
  onlineUsers: UserPresence[]
  
  // Actions para conversas
  setConversations: (conversations: DirectConversation[]) => void
  addConversation: (conversation: DirectConversation) => void
  updateConversation: (conversationId: string, updates: Partial<DirectConversation>) => void
  selectConversation: (conversationId: string | null) => void
  
  // Actions para mensagens
  setMessages: (messages: DirectMessage[]) => void
  addMessage: (message: DirectMessage) => void
  updateMessage: (messageId: string, updates: Partial<DirectMessage>) => void
  removeMessage: (messageId: string) => void
  prependMessages: (messages: DirectMessage[]) => void
  clearMessages: () => void
  
  // Actions para loading
  setLoadingMessages: (isLoading: boolean) => void
  setHasMoreMessages: (hasMore: boolean) => void
  
  // Actions para contador de não lidas
  setTotalUnreadCount: (count: number) => void
  incrementTotalUnreadCount: () => void
  decrementUnreadCount: (conversationId: string, count: number) => void
  
  // Actions para presença online
  setOnlineUsers: (users: UserPresence[]) => void
}

export const useDMStore = create<DMState>((set) => ({
  conversations: [],
  selectedConversationId: null,
  messages: [],
  isLoadingMessages: false,
  hasMoreMessages: true,
  totalUnreadCount: 0,
  onlineUsers: [],

  setConversations: (conversations) =>
    set({ conversations }),

  addConversation: (conversation) =>
    set((state) => {
      const exists = state.conversations.some((c) => c.conversation_id === conversation.conversation_id)
      if (exists) return state

      return {
        conversations: [conversation, ...state.conversations],
      }
    }),

  updateConversation: (conversationId, updates) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.conversation_id === conversationId
          ? { ...conv, ...updates }
          : conv
      ),
    })),

  selectConversation: (conversationId) =>
    set({ selectedConversationId: conversationId }),

  setMessages: (messages) =>
    set({ messages }),

  addMessage: (message) =>
    set((state) => {
      const exists = state.messages.some((m) => m.id === message.id)
      if (exists) return state

      return {
        messages: [...state.messages, message],
      }
    }),

  updateMessage: (messageId, updates) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      ),
    })),

  removeMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== messageId),
    })),

  prependMessages: (messages) =>
    set((state) => {
      const existingIds = new Set(state.messages.map((m) => m.id))
      const newMessages = messages.filter((m) => !existingIds.has(m.id))

      return {
        messages: [...newMessages, ...state.messages],
      }
    }),

  clearMessages: () =>
    set({ messages: [], hasMoreMessages: true }),

  setLoadingMessages: (isLoading) =>
    set({ isLoadingMessages: isLoading }),

  setHasMoreMessages: (hasMore) =>
    set({ hasMoreMessages: hasMore }),

  setTotalUnreadCount: (count) =>
    set({ totalUnreadCount: count }),

  incrementTotalUnreadCount: () =>
    set((state) => ({ totalUnreadCount: state.totalUnreadCount + 1 })),

  decrementUnreadCount: (conversationId, count) =>
    set((state) => ({
      totalUnreadCount: Math.max(0, state.totalUnreadCount - count),
      conversations: state.conversations.map((conv) =>
        conv.conversation_id === conversationId
          ? { ...conv, unread_count: 0 }
          : conv
      ),
    })),

  setOnlineUsers: (users) =>
    set({ onlineUsers: users }),
}))

